import { CirclePlusIcon, MinusCircleIcon, XIcon } from "lucide-react-native";
import { useCallback, useRef } from "react";
import { Alert, Animated, Dimensions, Platform } from "react-native";
import { Swipeable } from "react-native-gesture-handler";

import ShareActionItemView from "./ShareActionItemView.native";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import {
  UserWatchlistRole,
  useWatchlistCreateUserWatchlistMutation,
  useWatchlistDeleteUserWatchlistMutation,
  WatchlistMyUserWatchlistFragment,
  WatchlistMyUserWatchlistFragmentDoc,
  WatchlistUserFragment,
  WatchlistUserWatchlistFragment,
  UserWatchlistGrantSource,
} from "@/generated/graphql";

// design-system: dynamic-style waiver — animated interpolations (swipe translateX + scaleX)
const swipeActionStyle = (
  translateX: Animated.AnimatedInterpolation<number>,
  scale: Animated.AnimatedInterpolation<number>
) => ({
  width: 100,
  transform: [{ translateX }, { scaleX: scale }],
});

/**
 * Whether an Apollo `storeFieldName` for `listUserWatchlistsByWatchlistId`
 * targets the given watchlist. Parses the serialized args (everything after
 * the first `:`) and matches `watchlistId` exactly — a substring check could
 * leak a cache.modify keyed on the bare field name into another watchlist's
 * connection (e.g. `wl-1` vs `wl-10`). (SE-4901)
 * @param storeFieldName - Apollo store field name, e.g. `listUserWatchlistsByWatchlistId:{...}`
 * @param watchlistId - The watchlist ID the mutation is updating
 * @returns True when the store field's args target this watchlist
 */
const isWatchlistStoreField = (
  storeFieldName: string,
  watchlistId: string
): boolean => {
  const argsStart = storeFieldName.indexOf(":");
  if (argsStart === -1) return false;
  try {
    const args = JSON.parse(storeFieldName.slice(argsStart + 1)) as {
      watchlistId?: string;
    };
    return args.watchlistId === watchlistId;
  } catch {
    return false;
  }
};

/**
 * Props for the ShareActionItemContainer component
 */
interface Props {
  user: WatchlistUserFragment;
  isMyWatchlist: boolean;
  userWatchlist: WatchlistUserWatchlistFragment | null | undefined;
  watchlistId: string;
  isMeInWatchlist: boolean;
  onCloseShare: () => void;
}

/**
 * Container component that manages sharing watchlists with users, including add/remove functionality
 * @param root0 - Component props
 * @param root0.user - User data fragment for the watchlist member
 * @param root0.isMyWatchlist - Boolean flag indicating if the current user owns this watchlist
 * @param root0.userWatchlist - Existing user watchlist relationship, if any
 * @param root0.watchlistId - Unique identifier of the watchlist being shared
 * @param root0.isMeInWatchlist - Boolean flag indicating if the current user is in this watchlist
 * @param root0.onCloseShare - Callback function invoked when closing the share sheet
 */
const ShareActionItemContainer = ({
  user,
  isMyWatchlist,
  userWatchlist,
  watchlistId,
  isMeInWatchlist,
  onCloseShare,
}: Props) => {
  const isCallDeleting = useRef(false); // Trigger to call deleteUserWatchlist once time when user swipe right
  const isAlready = !!userWatchlist;
  const [createUserWatchlist, { loading: createLoading }] =
    useWatchlistCreateUserWatchlistMutation({
      variables: {
        input: { watchlistId, userId: user.id, role: UserWatchlistRole.Viewer },
      },
      update: (cache, { data, errors }) => {
        const id = data?.createUserWatchlist.id;
        if (!!watchlistId && !!id && !errors) {
          cache.modify({
            id: cache.identify({ __typename: "Query", id: "ROOT_QUERY" }),
            fields: {
              // Bare field name + storeFieldName guard so the edge is added
              // to the connection regardless of the `first` pagination arg
              // the query used. Keying on the exact `{"watchlistId":"X"}`
              // store key missed callers that paginate with `first`, so the
              // optimistic add never reflected in the Share UI (SE-4901).
              listUserWatchlistsByWatchlistId(
                existingData: unknown,
                { storeFieldName }: { storeFieldName: string }
              ) {
                if (!isWatchlistStoreField(storeFieldName, watchlistId)) {
                  return existingData;
                }
                const data = existingData as {
                  edges: { cursor: string; node: { __ref: string } }[];
                  [key: string]: unknown;
                };
                return {
                  ...data,
                  edges: [
                    ...data.edges,
                    {
                      __typename: "UserWatchlistEdge",
                      cursor: "",
                      node: {
                        __ref: cache.identify({
                          __typename: "UserWatchlist",
                          id,
                        }),
                      },
                    },
                  ],
                };
              },
            },
          });
        }
      },

      optimisticResponse: {
        __typename: "Mutation",
        createUserWatchlist: {
          // SE-6336: REQUIRED. This mutation's payload is a pure fragment spread
          // (`createUserWatchlist { ...WatchlistUserWatchlist }`), so without a
          // __typename Apollo cannot match the fragment and writes NOTHING —
          // silently, with no "Missing field" diagnostic. The measured store
          // entry was an empty `createUserWatchlist({}): {}`, so the optimistic
          // update never appeared at all.
          __typename: "UserWatchlist",
          // SE-6336: must be unique PER ROW. This organism is a FlashList row
          // per organization user, and each row disables only ITSELF while its
          // own mutation is in flight — so sharing with user A and then user B
          // before A resolves runs two concurrent creates. A hardcoded
          // "temp-id" normalized both optimistic entries to
          // `UserWatchlist:temp-id`, and the second write clobbered the first
          // row's optimistic state.
          //
          // Keyed on the pair the entity actually represents, so it is
          // deterministic (a retry of the same row reuses its own id) rather
          // than random.
          id: `temp-${watchlistId}-${user.id}`,
          role: UserWatchlistRole.Viewer,
          effectiveRole: UserWatchlistRole.Viewer,
          grantSource: UserWatchlistGrantSource.Direct,
          isMembershipActive: true,
          user,
        },
      },
    });

  const [deleteUserWatchlist, { loading: deleteLoading }] =
    useWatchlistDeleteUserWatchlistMutation({
      variables: { input: { id: userWatchlist?.id ?? "" } },
      optimisticResponse: {
        __typename: "Mutation",
        deleteUserWatchlist: { ...userWatchlist! },
      },
      update: (cache, { data, errors }) => {
        const id = data?.deleteUserWatchlist?.id;
        if (!!watchlistId && !!id && !errors) {
          cache.modify({
            id: cache.identify({ __typename: "Query", id: "ROOT_QUERY" }),
            fields: {
              // Bare field name + storeFieldName guard — see the matching
              // comment in the create mutation above (SE-4901).
              listUserWatchlistsByWatchlistId(
                existingData: unknown,
                { storeFieldName }: { storeFieldName: string }
              ) {
                if (!isWatchlistStoreField(storeFieldName, watchlistId)) {
                  return existingData;
                }
                const data = existingData as {
                  edges: { node: { __ref: string } }[];
                  [key: string]: unknown;
                };
                return {
                  ...data,
                  edges: data.edges.filter(
                    (edge: { node: { __ref: string } }) =>
                      edge.node.__ref !==
                      cache.identify({ __typename: "UserWatchlist", id })
                  ),
                };
              },
            },
          });
        }
      },
    });

  const loading = createLoading || deleteLoading;

  const handlePress = useCallback(() => {
    if (isAlready) {
      return deleteUserWatchlist();
    } else {
      return createUserWatchlist();
    }
  }, [createUserWatchlist, deleteUserWatchlist, isAlready]);

  const handleRemoveMeFromWatchlist = useCallback(() => {
    deleteUserWatchlist({
      onCompleted: () => {
        isCallDeleting.current = false;
        onCloseShare();
      },
      onError: () => {
        isCallDeleting.current = false;
      },
      update: (cache, { data, errors }) => {
        const id = data?.deleteUserWatchlist?.id;
        if (!!watchlistId && !!id && !errors) {
          cache.modify({
            id: cache.identify({ __typename: "Query", id: "ROOT_QUERY" }),
            fields: {
              listMyUserWatchlists(existingUserWatchlists = []) {
                return {
                  ...existingUserWatchlists,
                  edges: existingUserWatchlists.edges.filter(
                    (userWatchlistRef: { node: { __ref: string } }) => {
                      const userWatchlist: WatchlistMyUserWatchlistFragment | null =
                        cache.readFragment({
                          id: userWatchlistRef.node.__ref,
                          fragment: WatchlistMyUserWatchlistFragmentDoc,
                          fragmentName: "WatchlistMyUserWatchlist",
                        });
                      return userWatchlist?.watchlist.id !== watchlistId;
                    }
                  ),
                };
              },
            },
          });
        }
      },
    });
  }, [deleteUserWatchlist, onCloseShare, watchlistId]);

  const handleSwipeRight = useCallback(
    (direction: "right" | "left", swipeable: Swipeable | null) => {
      swipeable?.state?.rowTranslation?.addListener(({ value }) => {
        if (
          direction === "right" &&
          value < -140 &&
          isCallDeleting.current === false
        ) {
          isCallDeleting.current = true;
          handleRemoveMeFromWatchlist();
        }
      });
    },
    [handleRemoveMeFromWatchlist]
  );

  const handlePressDelete = useCallback(() => {
    if (Platform.OS === "web") {
      const doDelete = window.confirm(
        `Are you sure you want to leave this list?`
      );
      return doDelete ? handleRemoveMeFromWatchlist() : null;
    } else {
      Alert.alert(`Leave List`, `Are you sure you want to leave this list?`, [
        {
          text: "Cancel",
          style: "cancel",
        },
        {
          text: "Yes",
          onPress: () => {
            handleRemoveMeFromWatchlist();
          },
          style: "destructive",
        },
      ]);
    }
  }, [handleRemoveMeFromWatchlist]);

  const renderRightActions = useCallback(
    (
      progress: Animated.AnimatedInterpolation<number>,
      drag: Animated.AnimatedInterpolation<number>
    ) => {
      const translateX = progress.interpolate({
        inputRange: [0, 1],
        outputRange: [Dimensions.get("window").width, 0],
      });

      const scale = drag.interpolate({
        inputRange: [-150, -100, 0],
        outputRange: [1.5, 0.8, 0],
      });

      return (
        // eslint-disable-next-line no-restricted-syntax -- Animated.View carries live swipe interpolation transforms (translateX + scaleX); non-atomizable
        <Animated.View style={swipeActionStyle(translateX, scale)}>
          <Pressable
            className="flex-1 items-center justify-center bg-status-error p-8"
            onPress={handlePressDelete}
          >
            <Icon as={XIcon} size="xl" />
          </Pressable>
        </Animated.View>
      );
    },
    [handlePressDelete]
  );

  const iconActionSheet = isAlready ? MinusCircleIcon : CirclePlusIcon;

  return (
    <ShareActionItemView
      user={user}
      isMyWatchlist={isMyWatchlist}
      onPress={handlePress}
      loading={loading}
      isMeInWatchlist={isMeInWatchlist}
      renderRightActions={renderRightActions}
      onDelete={handlePressDelete}
      onSwipeableOpenStartDrag={handleSwipeRight}
      IconActionSheet={iconActionSheet}
    />
  );
};

export default ShareActionItemContainer;
